/**
 * Modulation target enumeration.
 *
 * Targets split into two scopes:
 *
 *   - `voice` — the AudioParam lives on each Voice instance. Modulating it
 *     fans out per-voice via Voice.getAudioParam. This is the classic "LFO
 *     wobbles every note's filter cutoff" case.
 *
 *   - `global` — the AudioParam lives on a single global module instance
 *     (effect). Only one connection per modulation source → one AudioParam;
 *     no per-voice duplication. Provided via each target's `getAudioParam`
 *     closure, captured at enumerate-time.
 *
 * Effects are enumerated dynamically — `globals.effects` is a list of
 * effect instances (multiple of the same type are fine). When more than
 * one of the same type exists the labels are suffixed with " 1", " 2"
 * so users can tell them apart in the Mod Matrix dropdown.
 *
 * Per-note sources (velocity, keytrack) can't drive global targets in a
 * meaningful way — each voice would add its contribution and values would
 * sum non-musically. The router enforces this at wire time.
 */

import type { SynthModule } from '../modules/SynthModule';
import type { VoiceTemplate } from '../Voice';
import type { MacroBank } from '../../macros/MacroBank';
import type { NumericParameter } from '../../params/Parameter';

export type ModTargetScope = 'voice' | 'global';

export interface ModTarget {
  readonly moduleId: string;
  readonly param: string;
  readonly label: string;
  /**
   * Short category tag used by the Mod Matrix dropdown to group targets
   * into optgroups (OSC, FILT, ENV, etc.). Just a display hint — the
   * router doesn't care.
   */
  readonly category: string;
  /**
   * Multiplier between the source's ±1 signal and the AudioParam's native
   * units. See PLAN.md §5 for the per-target tuning rationale.
   */
  readonly modScale: number;
  readonly scope: ModTargetScope;
  /**
   * For `global` targets, returns the AudioParam to modulate at wire time.
   * Omitted for `voice` targets (those resolve through Voice.getAudioParam).
   */
  readonly getAudioParam?: () => AudioParam | null;
  /**
   * Returns the underlying NumericParameter for this target. Used by the
   * macro wiring path, which drives target values visibly instead of
   * summing a hidden audio-rate signal. Both voice and global targets can
   * provide this — voice params live on the template modules, global
   * params on the effect modules.
   */
  readonly getParameter?: () => NumericParameter | null;
}

/**
 * A module that exposes mod-target AudioParams directly (effects). Implements
 * `getAudioParam(paramName)` returning the native AudioParam for that name.
 */
export interface GlobalTargetProvider extends SynthModule {
  getAudioParam(paramName: string): AudioParam | null;
}

export interface GlobalTargets {
  /**
   * LFO module — exposed so its Rate is a mod destination (useful for
   * S&H modulating an LFO's speed, or a macro sweeping LFO rate).
   */
  readonly lfo?: GlobalTargetProvider;
  /** Sample & Hold module — Rate and Slew exposed as mod destinations. */
  readonly sh?: SynthModule;
  /**
   * Noise module — Level exposed as a mod destination. Only getParameter
   * is wired (noise is per-voice, so there's no single AudioParam).
   */
  readonly noise?: SynthModule;
  /**
   * Dynamic list of effect instances. Each instance contributes its
   * own optgroup of params; identical types are disambiguated by a
   * trailing index (DIST 1 / DIST 2). Position in the list doesn't
   * matter for wiring — the router resolves by moduleId.
   */
  readonly effects?: readonly GlobalTargetProvider[];
}

/**
 * Voice-scope mod targets. These all live on template modules, so their
 * Parameters are shared — writing one cascades to every voice via the
 * existing bindAudioParam chain. Audio-rate modulation resolves through
 * `Voice.getAudioParam`; params without an AudioParam (env timings,
 * coarse pitch) still appear so macros can drive them.
 */
function voiceTargets(template: VoiceTemplate): ModTarget[] {
  return [
    // ── OSC ───────────────────────────────────────────────────────────
    {
      moduleId: template.osc.id,
      param: 'Coarse',
      label: 'OSC · Coarse',
      category: 'OSC',
      // Half an octave at depth=1 — enough to hear, not so much the
      // pitch runs away on typical LFO settings.
      modScale: 7,
      scope: 'voice',
      getParameter: () => template.osc.coarse,
    },
    {
      moduleId: template.osc.id,
      param: 'Detune',
      label: 'OSC · Detune',
      category: 'OSC',
      // Full semitone swing at depth=1. Classic vibrato feel.
      modScale: 100,
      scope: 'voice',
      getParameter: () => template.osc.detune,
    },
    {
      moduleId: template.osc.id,
      param: 'Level',
      label: 'OSC · Level',
      category: 'OSC',
      modScale: 1,
      scope: 'voice',
      getParameter: () => template.osc.level,
    },
    {
      moduleId: template.osc.id,
      param: 'Width',
      label: 'OSC · Width',
      category: 'OSC',
      // Width's AudioParam (widthDC.offset) is already in [-1,+1],
      // matching a unit-bipolar LFO. modScale=1 means full LFO swing
      // walks the pulse width through its silent endpoints; most
      // users will keep depth ≤ 0.5 for classic PWM swell.
      modScale: 1,
      scope: 'voice',
      getParameter: () => template.osc.width,
    },
    {
      moduleId: template.osc.id,
      param: 'Drive',
      label: 'OSC · Drive',
      category: 'OSC',
      // Drive is [0,1]. Voice exposes foldPreGain.gain as the
      // AudioParam — a positive LFO swing increases input drive
      // into the curve sample-by-sample, on top of the static
      // crossfade set by the param's display value. modScale=1
      // gives a full unit walk at depth=1.
      modScale: 1,
      scope: 'voice',
      getParameter: () => template.osc.drive,
    },
    {
      moduleId: template.osc.id,
      param: 'Fold',
      label: 'OSC · Fold',
      category: 'OSC',
      // Fold's curve is a static lookup table rebuilt when the
      // param's display value changes — there's no AudioParam to
      // ride. The router still drives the param via the display
      // channel for macros / slow LFOs; the curve gets rebuilt on
      // each write, which is fine at human knob-twist rates.
      modScale: 7, // [1..8] range; full LFO swing covers most of it
      scope: 'voice',
      getParameter: () => template.osc.fold,
    },

    // ── FILT ──────────────────────────────────────────────────────────
    {
      moduleId: template.filter.id,
      param: 'Cutoff',
      label: 'FILT · Cutoff',
      category: 'FILT',
      modScale: 4000,
      scope: 'voice',
      getParameter: () => template.filter.cutoff,
    },
    {
      moduleId: template.filter.id,
      param: 'Q',
      label: 'FILT · Q',
      category: 'FILT',
      modScale: 10,
      scope: 'voice',
      getParameter: () => template.filter.resonance,
    },

    // ── ENV ───────────────────────────────────────────────────────────
    // Env timings aren't AudioParams — they're read on note-on. Audio-rate
    // sources just wobble the knob visibly; macros drive them for real
    // (the next note hits with the new timing). That's the right trade
    // for "as many destinations as possible" — better an exposed target
    // that works with macros than a hidden one.
    {
      moduleId: template.env.id,
      param: 'Attack',
      label: 'ENV · Attack',
      category: 'ENV',
      modScale: 1,
      scope: 'voice',
      getParameter: () => template.env.attack,
    },
    {
      moduleId: template.env.id,
      param: 'Decay',
      label: 'ENV · Decay',
      category: 'ENV',
      modScale: 1,
      scope: 'voice',
      getParameter: () => template.env.decay,
    },
    {
      moduleId: template.env.id,
      param: 'Sustain',
      label: 'ENV · Sustain',
      category: 'ENV',
      modScale: 0.5,
      scope: 'voice',
      getParameter: () => template.env.sustain,
    },
    {
      moduleId: template.env.id,
      param: 'Release',
      label: 'ENV · Release',
      category: 'ENV',
      modScale: 1,
      scope: 'voice',
      getParameter: () => template.env.release,
    },

    // ── VCA ───────────────────────────────────────────────────────────
    {
      moduleId: template.vca.id,
      param: 'Gain',
      label: 'VCA · Gain',
      category: 'VCA',
      modScale: 1,
      scope: 'voice',
      getParameter: () => template.vca.gain,
    },
  ];
}

/** Find a NumericParameter by name on a SynthModule. Used for effect global targets. */
function findNumericParam(
  module: SynthModule,
  paramName: string,
): NumericParameter | null {
  const p = module.parameters.find((x) => x.name === paramName);
  if (p && p.kind === 'numeric') return p;
  return null;
}

/**
 * Per-effect-type descriptor. `params` is a list of "what to expose as a
 * mod target" entries — the prefix is left out since it's filled in at
 * enumerate time with the instance number when there's more than one of
 * a given type.
 */
interface EffectParamDesc {
  readonly param: string;
  readonly suffix: string;
  readonly modScale: number;
  /** `true` when the module exposes this param via its native AudioParam. */
  readonly audioParam?: boolean;
}

interface EffectTypeDesc {
  /** Short prefix used in labels and category tags ("DIST", "DELAY"…). */
  readonly tag: string;
  readonly params: readonly EffectParamDesc[];
}

/**
 * Per-effect-type parameter manifests. New effect types: add an entry
 * here so they appear in the Mod Matrix. The router doesn't care; this
 * is purely for the dropdown.
 */
const EFFECT_TYPE_DESCS: Readonly<Record<string, EffectTypeDesc>> = {
  distortion: {
    tag: 'DIST',
    params: [
      { param: 'Drive', suffix: 'Drive', modScale: 0.5 },
      { param: 'Tone', suffix: 'Tone', modScale: 4000, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
  delay: {
    tag: 'DLY',
    params: [
      { param: 'Time', suffix: 'Time', modScale: 0.5, audioParam: true },
      { param: 'Feedback', suffix: 'Fbk', modScale: 0.3, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.3 },
      // Pitch is a non-AudioParam (the shifter has its own
      // scheduler reading the value), so macros drive it via the
      // setNumeric path — perfect for "macro morphs from clean
      // delay to shimmer" sweeps. modScale of 12 = ±octave swing
      // at depth=1, which lines up with the parameter range
      // doubling smoothly across one octave.
      { param: 'Pitch', suffix: 'Pitch', modScale: 12 },
    ],
  },
  bitcrusher: {
    tag: 'CRUSH',
    params: [
      { param: 'Bits', suffix: 'Bits', modScale: 8, audioParam: true },
      { param: 'Freq', suffix: 'Freq', modScale: 10000, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
  reverb: {
    tag: 'REVERB',
    params: [
      { param: 'Size', suffix: 'Size', modScale: 2 },
      { param: 'Damp', suffix: 'Damp', modScale: 4000, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.3 },
      // Same scaling as DLY · Pitch — depth=1 swings ±octave so a
      // macro can morph from clean reverb into full shimmer over
      // one knob sweep.
      { param: 'Pitch', suffix: 'Pitch', modScale: 12 },
    ],
  },
  compressor: {
    tag: 'COMP',
    params: [
      { param: 'Threshold', suffix: 'Thresh', modScale: 20, audioParam: true },
      { param: 'Ratio', suffix: 'Ratio', modScale: 5, audioParam: true },
      { param: 'Makeup', suffix: 'Makeup', modScale: 0.5, audioParam: true },
    ],
  },
  eq: {
    tag: 'EQ',
    params: [
      // Frequencies live on BiquadFilterNode AudioParams — audio-rate
      // modulation gives smooth sweeps. modScale: a unit-bipolar LFO
      // at depth=1 swings the freq by this many Hz.
      { param: 'Lo Freq', suffix: 'Lo Hz', modScale: 400, audioParam: true },
      { param: 'Lo Gain', suffix: 'Lo Gain', modScale: 12, audioParam: true },
      { param: 'Mid Freq', suffix: 'Mid Hz', modScale: 4000, audioParam: true },
      { param: 'Mid Gain', suffix: 'Mid Gain', modScale: 12, audioParam: true },
      { param: 'Mid Q', suffix: 'Mid Q', modScale: 4, audioParam: true },
      { param: 'Hi Freq', suffix: 'Hi Hz', modScale: 6000, audioParam: true },
      { param: 'Hi Gain', suffix: 'Hi Gain', modScale: 12, audioParam: true },
    ],
  },
  chorus: {
    tag: 'CHORUS',
    params: [
      { param: 'Rate', suffix: 'Rate', modScale: 3, audioParam: true },
      { param: 'Depth', suffix: 'Depth', modScale: 0.5 },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
  flanger: {
    tag: 'FLANGE',
    params: [
      { param: 'Rate', suffix: 'Rate', modScale: 2, audioParam: true },
      { param: 'Depth', suffix: 'Depth', modScale: 0.5 },
      { param: 'Feedback', suffix: 'Fbk', modScale: 0.3, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
  phaser: {
    tag: 'PHASER',
    params: [
      { param: 'Rate', suffix: 'Rate', modScale: 3, audioParam: true },
      { param: 'Depth', suffix: 'Depth', modScale: 0.5 },
      { param: 'Feedback', suffix: 'Fbk', modScale: 0.3, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
  ringmod: {
    tag: 'RING',
    params: [
      // Full-depth LFO swinging ±1500 Hz makes ring mod especially vivid.
      { param: 'Freq', suffix: 'Freq', modScale: 1500, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
  wavefolder: {
    tag: 'FOLD',
    params: [
      { param: 'Drive', suffix: 'Drive', modScale: 0.5 },
      // Fold count drives audible character hard — 4 is enough swing
      // for an LFO to walk across the harmonic stack without clipping.
      { param: 'Fold', suffix: 'Fold', modScale: 4 },
      { param: 'Tone', suffix: 'Tone', modScale: 4000, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
  granular: {
    tag: 'GRAIN',
    params: [
      // All six DSP knobs route through worklet AudioParams, so audio-rate
      // mod from LFO/S&H/Velocity flows directly. Mix lives in the wrapper
      // (driving dry/wet GainNodes) and is macro-only.
      { param: 'Time', suffix: 'Time', modScale: 0.5, audioParam: true },
      { param: 'Feedback', suffix: 'Fbk', modScale: 0.3, audioParam: true },
      { param: 'Size', suffix: 'Size', modScale: 0.2, audioParam: true },
      { param: 'Density', suffix: 'Dens', modScale: 20, audioParam: true },
      { param: 'Spread', suffix: 'Sprd', modScale: 0.5, audioParam: true },
      // Full octave swing at depth=1 — same convention as DLY · Pitch /
      // REVERB · Pitch so an LFO at full depth walks one octave up/down.
      { param: 'Pitch', suffix: 'Pitch', modScale: 12, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
  ruin: {
    tag: 'RUIN',
    params: [
      // Tune is non-linear (Hz → 1/freq → delayTime), so don't expose
      // an AudioParam route — macro path drives it via setNumeric.
      { param: 'Tune', suffix: 'Tune', modScale: 500 },
      { param: 'Feedback', suffix: 'Fbk', modScale: 0.4, audioParam: true },
      { param: 'Damp', suffix: 'Damp', modScale: 4000, audioParam: true },
      { param: 'Mix', suffix: 'Mix', modScale: 0.5 },
    ],
  },
};

/**
 * Build the per-instance label/category for an effect, using the type
 * count map so duplicates get a numeric suffix:
 *
 *   one  DIST → tag "DIST",   label "DIST · Drive"
 *   two  DIST → tag "DIST 1", label "DIST 1 · Drive" (and tag "DIST 2", …)
 */
function effectInstanceTag(
  type: string,
  index: number,
  total: number,
): { category: string; prefix: string } | null {
  const desc = EFFECT_TYPE_DESCS[type];
  if (!desc) return null;
  const tag = total > 1 ? `${desc.tag} ${index + 1}` : desc.tag;
  return { category: tag, prefix: tag };
}

/** Targets contributed by one effect instance. */
function effectTargets(
  effect: GlobalTargetProvider,
  category: string,
  prefix: string,
): ModTarget[] {
  const desc = EFFECT_TYPE_DESCS[effect.type];
  if (!desc) return [];
  return desc.params.map((p) => {
    const target: ModTarget = {
      moduleId: effect.id,
      param: p.param,
      label: `${prefix} · ${p.suffix}`,
      category,
      modScale: p.modScale,
      scope: 'global',
      ...(p.audioParam
        ? { getAudioParam: () => effect.getAudioParam(p.param) }
        : {}),
      getParameter: () => findNumericParam(effect, p.param),
    };
    return target;
  });
}

/** Global-scope mod targets. Present only when the matching module exists. */
function globalTargets(globals: GlobalTargets): ModTarget[] {
  const out: ModTarget[] = [];

  // ── LFO (as a target) ───────────────────────────────────────────────
  // Modulating LFO.Rate from S&H or a macro is a classic "wiggly wiggle"
  // move — the LFO's own oscillator frequency is the AudioParam, so
  // audio-rate modulation works when the module exposes it.
  if (globals.lfo) {
    const mod = globals.lfo;
    out.push({
      moduleId: mod.id,
      param: 'Rate',
      label: 'LFO · Rate',
      category: 'LFO',
      modScale: 5,
      scope: 'global',
      getAudioParam: () => mod.getAudioParam('Rate'),
      getParameter: () => findNumericParam(mod, 'Rate'),
    });
  }

  // ── S&H (as a target) ───────────────────────────────────────────────
  // Rate and Slew aren't AudioParams — the setTimeout schedule reads
  // Rate.value on each tick, so macros actually move the step rate
  // audibly; audio-rate sources just wiggle the knob.
  if (globals.sh) {
    const mod = globals.sh;
    out.push({
      moduleId: mod.id,
      param: 'Rate',
      label: 'S&H · Rate',
      category: 'S&H',
      modScale: 10,
      scope: 'global',
      getParameter: () => findNumericParam(mod, 'Rate'),
    });
    out.push({
      moduleId: mod.id,
      param: 'Slew',
      label: 'S&H · Slew',
      category: 'S&H',
      modScale: 0.5,
      scope: 'global',
      getParameter: () => findNumericParam(mod, 'Slew'),
    });
  }

  // ── NOISE ───────────────────────────────────────────────────────────
  // Noise's Level Parameter is bound to every voice's per-voice noiseGain
  // via bindAudioParam multi-target, so audio-rate mod flows naturally
  // through the macro path (writing Level cascades into all voices). We
  // only expose getParameter — there's no single AudioParam to modulate
  // (one per voice).
  if (globals.noise) {
    const mod = globals.noise;
    out.push({
      moduleId: mod.id,
      param: 'Level',
      label: 'NOISE · Level',
      category: 'NOISE',
      modScale: 0.5,
      scope: 'global',
      getParameter: () => findNumericParam(mod, 'Level'),
    });
  }

  // ── EFFECTS — dynamic enumeration ───────────────────────────────────
  // Walk every effect in the chain, emitting its params with a category
  // tag that disambiguates duplicates (DIST 1 / DIST 2). The router only
  // uses moduleId+param, so labels can change without breaking wiring.
  const effects = globals.effects ?? [];
  // Per-type counts, used to know which instances need a numeric suffix.
  const totalsByType = new Map<string, number>();
  for (const e of effects) totalsByType.set(e.type, (totalsByType.get(e.type) ?? 0) + 1);
  // Per-type running index — the i-th instance of a given type gets
  // suffix " (i+1)" when totals > 1.
  const indexByType = new Map<string, number>();
  for (const eff of effects) {
    const idx = indexByType.get(eff.type) ?? 0;
    indexByType.set(eff.type, idx + 1);
    const total = totalsByType.get(eff.type) ?? 1;
    const tag = effectInstanceTag(eff.type, idx, total);
    if (!tag) continue;
    out.push(...effectTargets(eff, tag.category, tag.prefix));
  }

  return out;
}

/**
 * Macro-scope mod targets. Each macro's value NumericParameter is exposed as a
 * target so macros can drive other macros (chained routing). Treated as
 * global-scope — there's one Parameter per macro, not one per voice — but no
 * AudioParam is exposed; macro targets only wire via the macro path, so
 * audio-rate sources (LFO, S&H, per-note) silently skip them. That's a known
 * v1 limitation: macro targets are only driven by macro sources today.
 */
function macroTargets(macroBank: MacroBank): ModTarget[] {
  return macroBank.macros.map((m) => ({
    moduleId: m.id,
    param: 'Value',
    label: `${m.label} · Value`,
    category: 'MACRO',
    // Macro values are 0..1; the source macro is also 0..1. Unit scale keeps
    // "depth=1" meaning "full swing", which matches the user's mental model
    // of a macro knob.
    modScale: 1,
    scope: 'global' as const,
    getParameter: () => m.value,
  }));
}

export function enumerateTargets(
  template: VoiceTemplate,
  globals: GlobalTargets = {},
  macroBank?: MacroBank,
): ModTarget[] {
  return [
    ...voiceTargets(template),
    ...globalTargets(globals),
    ...(macroBank ? macroTargets(macroBank) : []),
  ];
}

/** Look up a target's modScale. 1 for unknown targets (caller's safety net). */
export function getTargetScale(
  targets: readonly ModTarget[],
  moduleId: string,
  param: string,
): number {
  return (
    targets.find((t) => t.moduleId === moduleId && t.param === param)?.modScale ?? 1
  );
}

/** Look up a full target entry. Used by the router to discriminate scope. */
export function findTarget(
  targets: readonly ModTarget[],
  moduleId: string,
  param: string,
): ModTarget | undefined {
  return targets.find((t) => t.moduleId === moduleId && t.param === param);
}
